import { useEffect, useMemo, useState } from "react";
import { fetchSiteMedia } from "../api/media";

let cachedMedia = null;
let inflight = null;
const MEDIA_CACHE_GEN = 1;

function emptyState() {
  return { items: [], byKey: {}, ready: false };
}

export function useSiteMedia(videoKeys, imageKeys) {
  const [media, setMedia] = useState(cachedMedia || emptyState());

  useEffect(() => {
    let cancelled = false;

    if (cachedMedia?.ready && cachedMedia.gen === MEDIA_CACHE_GEN) {
      setMedia(cachedMedia);
      return undefined;
    }

    if (!inflight) {
      inflight = fetchSiteMedia()
        .then((data) => {
          cachedMedia = { ...data, ready: true, gen: MEDIA_CACHE_GEN };
          return cachedMedia;
        })
        .catch(() => ({ items: [], byKey: {}, ready: false }))
        .finally(() => {
          inflight = null;
        });
    }

    inflight.then((next) => {
      if (!cancelled) setMedia(next);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const url = (key) => media.byKey[key]?.publicUrl || "";

  const graduateVideos = useMemo(() => {
    const restrictVideos = Array.isArray(videoKeys) && videoKeys.length > 0;
    const items = restrictVideos
      ? videoKeys.map((key) => media.byKey[key]).filter(Boolean)
      : media.items
          .filter((item) => item.group === "graduate-videos")
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    return items
      .filter((item) => item.publicUrl)
      .map((item) => ({
        key: item.key,
        src: item.publicUrl,
        name: item.meta?.name || item.alt || item.key,
        role: item.meta?.role || "",
      }));
  }, [media.items, media.byKey, videoKeys]);

  const extraImages = useMemo(() => {
    return media.items
      .filter((item) => item.type === "image" && item.group === "uploads" && item.publicUrl)
      .filter((item) => !Array.isArray(imageKeys) || imageKeys.length === 0 || imageKeys.includes(item.key))
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      .map((item) => ({
        key: item.key,
        src: item.publicUrl,
        alt: item.alt || item.key,
      }));
  }, [media.items, imageKeys]);

  return { ...media, url, graduateVideos, extraImages };
}
